import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import {
  HeartPulse, Building2, Scale, Utensils, Hotel, BarChart2, Store,
  CheckCircle2, ArrowRight
} from 'lucide-react';
import LocaleLink from '../components/LocaleLink';

const VISION_IMG = '/media/vision.png';

const content = {
  ar: {
    hero: {
      badge: 'الرؤية',
      h1a: 'ليس مجرد منتج.',
      h1b: 'بنية تحتية.',
      sub: 'تُبنى كويدوري لتكون العمود الفقري للذكاء الاصطناعي والأتمتة للأعمال الرقمية متعددة اللغات عبر صناعات متعددة.',
    },
    mission: {
      badge: 'مهمتنا',
      h: 'مبنية للعربية. مصممة للعالم.',
      p1: 'تعامل معظم منصات الذكاء الاصطناعي العربية كأمر ثانوي. كويدوري تبدأ بالعربية — تتعامل معها بنفس العمق اللغوي والسياق الثقافي والدقة التقنية كأي لغة عالمية رئيسية.',
      p2: 'هذا ليس ترجمة. هذا ذكاء أصيل.',
      points: ['العربية ليست ترجمة في كويدوري — إنها لغة نواة', 'مصطلحات المجال والفروق الثقافية مُدمجة مباشرة', 'مثالي لجماهير الشرق الأوسط وشمال أفريقيا وما بعدها'],
    },
    roadmap: {
      badge: 'خارطة الطريق',
      h: 'ثلاث مراحل. رؤية واحدة.',
      phases: [
        { phase: 'المرحلة ١', title: 'الذكاء المالي', current: true, items: ['QuidPulse', 'Quidanalyzer', 'Post Publisher', 'Video Publisher'] },
        { phase: 'المرحلة ٢', title: 'وكلاء الأعمال الذكيون', current: false, items: ['وكلاء ذكاء اصطناعي للعيادات', 'وكلاء ذكاء اصطناعي للعقارات', 'وكلاء ذكاء اصطناعي للمطاعم', 'وكلاء ذكاء اصطناعي لمكاتب المحاماة'] },
        { phase: 'المرحلة ٣', title: 'منصة العلامة التجارية الخاصة', current: false, items: ['وكلاء ذكاء اصطناعي للفنادق', 'وكلاء ذكاء اصطناعي للمالية', 'وكلاء الأعمال المحلية', 'بنية تحتية مخصصة'] },
      ],
    },
    industries: {
      badge: 'التوسع المستقبلي',
      h: 'مصمم لكل صناعة',
      sub: 'نفس البنية التحتية الذكية ستُشغّل تحولاً رقمياً في صناعات متعددة.',
      items: [
        { name: 'العيادات', icon: HeartPulse },
        { name: 'العقارات', icon: Building2 },
        { name: 'مكاتب المحاماة', icon: Scale },
        { name: 'المطاعم', icon: Utensils },
        { name: 'الفنادق', icon: Hotel },
        { name: 'المالية', icon: BarChart2 },
        { name: 'الأعمال المحلية', icon: Store },
      ],
    },
    values: {
      badge: 'مبادئنا',
      h: 'ما يوجّه قراراتنا',
      items: [
        { title: 'العربية أولاً', desc: 'العربية ليست أمراً ثانوياً. إنها الأساس.' },
        { title: 'الذكاء على الحجم', desc: 'نُحسّن للذكاء المعنوي والمُحدد بالسياق — ليس حجم البيانات الخام.' },
        { title: 'التفكير في البنية التحتية', desc: 'كل قرار يتعلق بالمنتج يُتخذ مع وضع قابلية توسع المنصة في الاعتبار.' },
        { title: 'الأناقة التشغيلية', desc: 'تعقيد تحت الغطاء، بساطة للمستخدم.' },
      ],
    },
    cta: { h: 'ابدأ رحلتك مع كويدوري', sub: 'كن جزءاً من الجيل القادم من الأعمال الذكية متعددة اللغات.', btn: 'ابدأ مجاناً' },
  },
  en: {
    hero: {
      badge: 'Vision',
      h1a: 'Not just a product.',
      h1b: 'An infrastructure.',
      sub: "Quidory is being built as the AI intelligence and automation backbone for multilingual digital businesses across multiple industries.",
    },
    mission: {
      badge: 'Our Mission',
      h: 'Built for Arabic. Designed for the World.',
      p1: "Most AI platforms treat Arabic as an afterthought. Quidory starts with Arabic — treating it with the same linguistic depth, cultural context, and technical precision as any major global language.",
      p2: "This is not translation. This is native intelligence.",
      points: ['Arabic is a core language in Quidory, not a translation', 'Domain terminology and cultural nuance built in', 'Optimized for MENA audiences and beyond'],
    },
    roadmap: {
      badge: 'Roadmap',
      h: 'Three Phases. One Vision.',
      phases: [
        { phase: 'Phase 1', title: 'Financial Intelligence', current: true, items: ['QuidPulse', 'Quidanalyzer', 'Post Publisher', 'Video Publisher'] },
        { phase: 'Phase 2', title: 'Business AI Agents', current: false, items: ['Clinic AI Agents', 'Real Estate AI Agents', 'Restaurant AI Agents', 'Law Firm AI Agents'] },
        { phase: 'Phase 3', title: 'White-Label Platform', current: false, items: ['Hotel AI Agents', 'Finance AI Agents', 'Local Business Agents', 'Custom AI Infrastructure'] },
      ],
    },
    industries: {
      badge: 'Future Expansion',
      h: 'Designed for Every Industry',
      sub: 'The same intelligent infrastructure will power digital transformation across multiple industries.',
      items: [
        { name: 'Clinics', icon: HeartPulse },
        { name: 'Real Estate', icon: Building2 },
        { name: 'Law Firms', icon: Scale },
        { name: 'Restaurants', icon: Utensils },
        { name: 'Hotels', icon: Hotel },
        { name: 'Finance', icon: BarChart2 },
        { name: 'Local Business', icon: Store },
      ],
    },
    values: {
      badge: 'Our Principles',
      h: 'What Guides Our Decisions',
      items: [
        { title: 'Arabic First', desc: 'Arabic is not an afterthought. It is the foundation.' },
        { title: 'Intelligence Over Volume', desc: 'We optimize for meaningful, contextualized intelligence — not raw data volume.' },
        { title: 'Infrastructure Thinking', desc: 'Every product decision is made with platform scalability in mind.' },
        { title: 'Operational Elegance', desc: 'Complexity under the hood, simplicity for the user.' },
      ],
    },
    cta: { h: 'Start Your Quidory Journey', sub: 'Join the next generation of multilingual intelligent businesses.', btn: 'Start Free' },
  },
};

const AboutPage = () => {
  const { language } = useLanguage();
  const c = content[language];

  return (
    <div className="pt-16">
      <SEO
        titleEn="About Quidory — AI Infrastructure for Every Industry"
        titleAr="عن كويدوري — بنية تحتية ذكاء اصطناعي لكل صناعة"
        descEn="Quidory is being built as the AI intelligence and automation backbone for multilingual digital businesses across multiple industries."
        descAr="تُبنى كويدوري لتكون العمود الفقري للذكاء الاصطناعي والأتمتة للأعمال الرقمية متعددة اللغات عبر صناعات متعددة."
        path="/about"
      />
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${VISION_IMG})` }}
        />
        <div className="absolute inset-0 bg-background/85 dark:bg-[#050814]/90" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-4 block">{c.hero.badge}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground mb-6">
            {c.hero.h1a}{' '}<span className="text-gold font-medium">{c.hero.h1b}</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">{c.hero.sub}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">{c.mission.badge}</span>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-5">{c.mission.h}</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">{c.mission.p1}</p>
              <p className="text-foreground font-medium mb-6">{c.mission.p2}</p>
              <ul className="space-y-2.5">
                {c.mission.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/10 to-transparent rounded-3xl" />
              <blockquote className="relative p-8 rounded-2xl border border-gold/20 bg-card">
                <div className="text-5xl text-gold/30 font-serif mb-4">"</div>
                <p className="text-xl font-light text-foreground leading-relaxed mb-4">
                  {language === 'ar'
                    ? 'الذكاء الاصطناعي بطبيعته متعدد اللغات — لكن تطبيقاته نادراً ما تكون كذلك. نحن هنا لتغيير هذا.'
                    : "AI is inherently multilingual — but its applications rarely are. We're here to change that."
                  }
                </p>
                <cite className="text-sm text-muted-foreground not-italic">— {language === 'ar' ? 'فريق كويدوري' : 'Quidory Team'}</cite>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-24 bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">{c.roadmap.badge}</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground">{c.roadmap.h}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {c.roadmap.phases.map((ph, i) => (
              <div
                key={i}
                data-testid={`roadmap-phase-${i}`}
                className={`p-6 rounded-2xl border transition-all duration-200 ${
                  ph.current
                    ? 'border-gold/40 bg-background shadow-sm'
                    : 'border-border bg-background/50 opacity-75'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gold">{ph.phase}</span>
                  {ph.current && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gold/15 text-gold border border-gold/30">
                      {language === 'ar' ? 'الحالي' : 'Current'}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-medium text-foreground mb-4">{ph.title}</h3>
                <ul className="space-y-2">
                  {ph.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${ph.current ? 'bg-gold' : 'bg-border'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">{c.industries.badge}</span>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-4">{c.industries.h}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-12">{c.industries.sub}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            {c.industries.items.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div key={i} className="flex flex-col items-center gap-2.5 p-4 rounded-xl border border-border hover:border-gold/30 hover:bg-card transition-all duration-200">
                  <div className="p-2.5 rounded-lg bg-gold/10">
                    <Icon className="h-5 w-5 text-gold" />
                  </div>
                  <span className="text-xs text-muted-foreground text-center font-medium">{ind.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">{c.values.badge}</span>
            <h2 className="text-3xl font-light tracking-tight text-foreground">{c.values.h}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.values.items.map((val, i) => (
              <div key={i} className="p-6 rounded-xl border border-border bg-background hover:border-gold/20 transition-all duration-200">
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                  <span className="text-gold font-semibold text-sm">{i + 1}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-2">{val.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background border-t border-border text-center">
        <h2 className="text-3xl font-light text-foreground mb-3">{c.cta.h}</h2>
        <p className="text-muted-foreground mb-8">{c.cta.sub}</p>
        <LocaleLink
          to="/signup"
          data-testid="about-cta-btn"
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-all duration-200"
        >
          {c.cta.btn}
          <ArrowRight className="h-4 w-4" />
        </LocaleLink>
      </section>
    </div>
  );
};

export default AboutPage;
